import ReactGA from 'react-ga4';

// Get Measurement ID from environment variable
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Initialize Google Analytics 4
 * Call this once when the app starts
 */
export const initGA = () => {
  if (!MEASUREMENT_ID) {
    console.warn('GA4 Measurement ID not found. Analytics will not be initialized.');
    return;
  }

  try {
    ReactGA.initialize(MEASUREMENT_ID, {
      gaOptions: {
        debug_mode: process.env.NODE_ENV === 'development', // Enable debug mode in development
      },
      gtagOptions: {
        send_page_view: false, // We'll manually track page views for better control
      },
    });

    console.log('Google Analytics initialized:', MEASUREMENT_ID);
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track page views
 * @param {string} path - Page path (e.g., '/dashboard')
 * @param {string} title - Page title (optional)
 */
export const logPageView = (path, title) => {
  if (!MEASUREMENT_ID) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });

    console.log('Page view tracked:', path);
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track custom events
 * @param {string} category - Event category (e.g., 'Chat', 'Authentication')
 * @param {string} action - Event action (e.g., 'message_sent', 'signup_completed')
 * @param {object} params - Additional event parameters
 */
export const logEvent = (category, action, params = {}) => {
  if (!MEASUREMENT_ID) return;

  try {
    ReactGA.event({
      category,
      action,
      ...params,
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('Event tracked:', { category, action, params });
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Set user properties (called after login)
 * @param {object} properties - User properties
 */
export const setUserProperties = (properties) => {
  if (!MEASUREMENT_ID) return;

  try {
    ReactGA.set(properties);

    console.log('User properties set:', properties);
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

/**
 * Set user ID (for User-ID tracking)
 * @param {string|number} userId - Unique user identifier (use hashed email or numeric ID)
 */
export const setUserId = (userId) => {
  if (!MEASUREMENT_ID || !userId) return;

  try {
    ReactGA.set({ userId: String(userId) });

    console.log('User ID set:', userId);
  } catch (error) {
    console.error('Failed to set user ID:', error);
  }
};

/**
 * Track timing events (for performance monitoring)
 * @param {string} category - Timing category
 * @param {string} variable - Timing variable name
 * @param {number} value - Time value in milliseconds
 */
export const logTiming = (category, variable, value) => {
  if (!MEASUREMENT_ID) return;

  try {
    ReactGA.event({
      category: 'timing',
      action: category,
      label: variable,
      value: Math.round(value),
    });

    console.log('Timing tracked:', { category, variable, value });
  } catch (error) {
    console.error('Failed to track timing:', error);
  }
};

/**
 * Helper: Hash email for privacy (simple hash)
 * Use this to anonymize email addresses before sending to GA
 * @param {string} email - Email address
 * @returns {string} - Hashed email
 */
export const hashEmail = (email) => {
  if (!email) return '';

  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `user_${Math.abs(hash)}`;
};

// Export default object with all functions
export default {
  initGA,
  logPageView,
  logEvent,
  setUserProperties,
  setUserId,
  logTiming,
  hashEmail,
};
